// Direct manipulation: grab a cubie and drag to turn a layer. The turn follows
// the pointer; on release it commits (or springs back) through the animator.

import * as THREE from "three";
import { makeMove } from "../cube/moves.js";
import { AXIS_ROTATION_SIGN } from "./turn-animator.js";

export class DragTurn {
  constructor(
    view,
    camera,
    { beginTurn, commitTurn, enabled = () => true } = {},
  ) {
    this.view = view;
    this.camera = camera;
    this.beginTurn = beginTurn; // (move, fromAngle) -> ok
    this.commitTurn = commitTurn; // (move) after drag release
    this.enabled = enabled;
    this.drag = null;
    this._v1 = new THREE.Vector3();
    this._v2 = new THREE.Vector3();
    this._v3 = new THREE.Vector3();
    this._axis = new THREE.Vector3();
    this._center = new THREE.Vector3(0, 0, 0);
  }

  down(ndc, point) {
    if (!this.enabled()) return false;
    const hit = this.view.pickCubie(ndc);
    if (!hit) return false;
    this.drag = {
      grab: hit,
      startX: point.x,
      startY: point.y,
      lastX: point.x,
      lastY: point.y,
      move: null,
      decided: false,
      totalAngle: 0,
    };
    return true;
  }

  move(point) {
    const d = this.drag;
    if (!d) return;
    const dx = point.x - d.lastX;
    const dy = point.y - d.lastY;
    d.lastX = point.x;
    d.lastY = point.y;

    if (!d.decided) {
      const totalDx = point.x - d.startX;
      const totalDy = point.y - d.startY;
      if (Math.hypot(totalDx, totalDy) < 8) return;
      d.decided = true;
      const axis = this._pickAxis(totalDx, totalDy);
      if (!axis) {
        this.drag = null;
        return;
      }
      d.axisVec = axis;
      d.axis = axis.x !== 0 ? 0 : axis.y !== 0 ? 1 : 2;
      d.axisDirection = axis.getComponent(d.axis);
      d.layer = d.grab[["x", "y", "z"][d.axis]];
      d.sign = this._pickSign(axis, totalDx, totalDy, d);
      const positiveSide = d.layer >= (this.view.N - 1) / 2;
      const face = positiveSide
        ? ["R", "U", "F"][d.axis]
        : ["L", "D", "B"][d.axis];
      const depth = positiveSide ? this.view.N - 1 - d.layer : d.layer;
      const worldTurnSign = d.sign * d.axisDirection;
      const internalTurns =
        worldTurnSign === AXIS_ROTATION_SIGN[d.axis] ? 1 : 3;
      const turns = positiveSide ? internalTurns : 4 - internalTurns;
      const move = makeMove(this.view.N, { face, depth, times: turns });
      d.move = move;
      this.beginTurn && this.beginTurn(move, 0);
    }

    if (!d.move) return;
    const q = this._v1.set(d.grab.x, d.grab.y, d.grab.z).sub(this._halfVec());
    const e = d.axisVec;
    // Screen-space motion direction of q under a positive rotation about e.
    const motion = this._v2.copy(e).cross(q).normalize();
    const p0 = this._project(q);
    const p1 = this._project(q.clone().addScaledVector(motion, 0.001));
    let dirX = p1.x - p0.x,
      dirY = p1.y - p0.y;
    const len = Math.hypot(dirX, dirY) || 1;
    dirX /= len;
    dirY /= len;
    // Signed drag component along the motion direction -> turn angle.
    const comp = screenDragDot(dx, dy, dirX, dirY);
    const scale = this._angleScale(q);
    d.totalAngle += comp * scale;
    const maxA = Math.PI / 2;
    d.totalAngle = THREE.MathUtils.clamp(d.totalAngle, -maxA, maxA);
    if (this.drag) {
      this.view.setTurnPose(d.move, d.totalAngle * d.axisDirection);
    }
  }

  up() {
    const d = this.drag;
    if (!d) return false;
    this.drag = null;
    if (!d.move) return false;
    const angle = Math.abs(d.totalAngle);
    if (angle >= Math.PI / 5) {
      // Commit with a completing animation.
      const move = { ...d.move };
      const from = d.totalAngle * d.axisDirection;
      this.commitTurn && this.commitTurn(move, from);
      return true;
    }
    // Spring back.
    this.view.resetPose();
    return false;
  }

  cancel() {
    this.drag = null;
    this.view.resetPose();
  }

  _halfVec() {
    const h = (this.view.N - 1) / 2;
    return this._v3.set(h, h, h);
  }

  _pickAxis(dx, dy) {
    const horizontal = Math.abs(dx) > Math.abs(dy);
    // Vertical screen axis for horizontal drags: camera up snapped to a world axis.
    const up = this._v1.set(0, 1, 0).applyQuaternion(this.camera.quaternion);
    const right = this._v2.set(1, 0, 0).applyQuaternion(this.camera.quaternion);
    const v = horizontal ? up : right;
    const abs = [Math.abs(v.x), Math.abs(v.y), Math.abs(v.z)];
    const i = abs.indexOf(Math.max(...abs));
    this._axis.set(0, 0, 0);
    this._axis.setComponent(i, v.getComponent(i) > 0 ? 1 : -1);
    return this._axis;
  }

  _pickSign(axisVec, dx, dy, d) {
    // Determine whether a positive rotation about axisVec moves the grabbed
    // cubie along the drag direction.
    const h = (this.view.N - 1) / 2;
    const q = this._v1.set(d.grab.x - h, d.grab.y - h, d.grab.z - h);
    const motion = this._v2.copy(axisVec).cross(q).normalize();
    const p0 = this._project(q);
    const p1 = this._project(q.clone().addScaledVector(motion, 0.01));
    const mx = p1.x - p0.x,
      my = p1.y - p0.y;
    const dot = screenDragDot(dx, dy, mx, my);
    return dot >= 0 ? 1 : -1;
  }

  _angleScale(q) {
    // Screen distance of the grabbed cubie from the rotation axis, in NDC,
    // scaled by viewport height to convert pixel motion to radians.
    const p0 = this._project(q);
    const c = this._project(new THREE.Vector3(0, 0, 0));
    const r = Math.hypot(p0.x - c.x, p0.y - c.y);
    if (r < 1e-4) return 0;
    const px = this.view.container.clientHeight / 2;
    return 1 / r / px;
  }

  _project(v) {
    return new THREE.Vector3().copy(v).project(this.camera);
  }
}

// Pointer Y grows downward while projected NDC Y grows upward. Keeping the
// conversion here makes horizontal and vertical gestures use one consistent
// screen-space convention.
export function screenDragDot(dx, dy, projectedX, projectedY) {
  return dx * projectedX - dy * projectedY;
}
